import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Add,
  Close as CloseIcon,
  CommentOutlined as CommentOutlinedIcon,
  EditOutlined as Edit,
  ErrorOutlined as ErrorOutlinedIcon,
} from "@mui/icons-material";
import CustomIconButton from "componentsNew/buttons/CustomIconButton";
import { ModalWindow } from "componentsNew/modals/ModalWindow";
import {
  DollyNumber,
  DriverData,
  FleetSizeType,
  RabbitNumber,
  ReportRecord,
} from "types/ShiftTracker";
import { openModal } from "actions/modalActions";
import {
  removeReportRecordAction,
  updateReportRecordStateAction,
  updateSerialNumberAction,
} from "actions/shiftTrackerActions";
import { ChangeEvent, FC, memo, useEffect, useState } from "react";
import { useDispatch, useStore } from "react-redux";
import momentTz from "moment-timezone";
import { roleDa, waveDa } from "mocks/roleDA";
import {
  EditShiftCommentModalContent,
  EditShiftVanModalContent,
} from "componentsNew/modals/modalContent/ShiftModelContent";
import api from "api";
import Alert, { Color } from "@material-ui/lab/Alert";
import {
  CANCELLED_ROLES,
  daShiftStatusConstants,
  EDITABLE_HOURS_ROLES,
  FIXED_HOURS_ROLES,
  fleetSizesConstantList,
  MENU_PROPS,
  NO_FLEET_SIZE,
} from "constants/constants";
import { useDaStatusHighlight } from "hooks/getDaStatusHighlight";
import { RootState } from "reducers/rootReducer";

type UpdateCell = (data: Record<string, any>) => void;

interface ShiftRecordCellProps {
  index: number;
  columnId: string;
  cellData: ReportRecord;
  shiftDate: string;
  shiftStation: number;
  classes: any;
  handleUpdateCell: UpdateCell;
  disabledCondition: boolean;
  disabledByDate: boolean;
}

const CELL_COMPARE_FIELDS: Record<string, (keyof ReportRecord)[]> = {
  id: ["driver", "second_driver", "is_status_published", "is_second_status_published"],
  rabbit_number: ["rabbit_number", "second_rabbit_number", "returned_rabbit"],
  dolly_number: ["dolly_number", "second_dolly_number", "returned_dolly"],
  role: ["role", "role_hours", "da_status", "second_driver", "second_da_status"],
  role_hours: ["role_hours", "role"],
  badge: ["driver"],
  transporter_id: ["driver"],
  da_status: ["driver", "role", "second_driver"],
  second_driver: ["driver"],
  second_da_status: ["driver", "role", "second_driver"],
  fleet_size: ["cancelled_fleet_size", "driver", "van", "assignments"],
  van: ["cancelled_fleet_size", "driver", "van", "assignments"],
  company: ["van"],
};

const areCellPropsEqual = (prev: ShiftRecordCellProps, next: ShiftRecordCellProps) => {
  if (
    prev.columnId !== next.columnId ||
    prev.index !== next.index ||
    prev.shiftStation !== next.shiftStation ||
    prev.disabledCondition !== next.disabledCondition ||
    prev.disabledByDate !== next.disabledByDate ||
    prev.handleUpdateCell !== next.handleUpdateCell ||
    prev.cellData.id !== next.cellData.id
  ) {
    return false;
  }

  const fields = CELL_COMPARE_FIELDS[prev.columnId] || [prev.columnId as keyof ReportRecord];
  return fields.every((field) => prev.cellData[field] === next.cellData[field]);
};

const getRolePatch = (role: number, cellData: ReportRecord, shiftStation: number) => {
  const fullRole = roleDa.find((el) => el.value === role) || null;
  
  const role_hours = role === 0 && Number(shiftStation) === 19 ? 9 : FIXED_HOURS_ROLES.includes(role)
    ? fullRole?.baseHours
    : cellData.role_hours || fullRole?.baseHours;

  const patch: Record<string, any> = { role, role_hours };

  if (CANCELLED_ROLES.includes(role)) {
    patch.da_status = 3;
    patch.is_status_published = false;
    if (cellData.second_driver) {
      patch.second_da_status = 3;
      patch.is_second_status_published = false;
    }
  } else if (role === 10) {
    patch.da_status = 2;
    patch.is_status_published = false;
  } else {
    if (cellData.da_status !== 1) {
      patch.da_status = 0;
      patch.is_status_published = false;
    }
    if (cellData.second_driver && cellData.second_da_status !== 1) {
      patch.second_da_status = 0;
      patch.is_second_status_published = false;
    }
  }

  return patch;
};

const getDaStatusPatch = (columnId: string, statusValue: number, cellData: ReportRecord) => {
  if (columnId !== "da_status") return { second_da_status: statusValue, is_second_status_published: false };

  return {
    da_status: statusValue,
    is_status_published: false,
    ...(statusValue === 3 && { role: 9 }),
    ...(statusValue === 2 && { role: 10 }),
  };
};

export const ShiftRecordCell = memo(
  ({
    index,
    columnId,
    cellData,
    shiftDate,
    shiftStation,
    classes,
    handleUpdateCell,
    disabledCondition,
    disabledByDate,
  }: ShiftRecordCellProps) => {
    const recordValue: any = cellData[columnId as keyof ReportRecord];
    const dispatch = useDispatch();
    const assignment = cellData.assignments?.[0];

    switch (columnId) {
      case "id": {
        const showUnpublished = Boolean((cellData.driver && !cellData.is_status_published) || (!cellData?.is_second_status_published && cellData?.second_driver));
        return (
          <Box style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ErrorOutlinedIcon
              style={{ color: "#0129FA", visibility: showUnpublished ? "visible" : "hidden" }}
            />
            <Typography>{index + 1}</Typography>
          </Box>
        );
      }
      case "driver":
      case "second_driver": {
        const isSecondDriver = columnId === "second_driver";
        return (
          <DriverField
            recordId={cellData.id}
            currentReportDate={shiftDate}
            currentReportStationId={shiftStation}
            driverValue={recordValue as DriverData}
            assignmentId={isSecondDriver ? 0 : assignment?.id}
            daStatus={isSecondDriver ? cellData.second_da_status : cellData.da_status}
            handleUpdateCell={handleUpdateCell}
            disabledCondition={
              isSecondDriver
                ? (disabledByDate && disabledCondition) || !cellData.driver
                : disabledByDate
                  ? disabledCondition
                  : cellData.da_status === 3
            }
            isSecondDriver={isSecondDriver}
          />
        );
      }
      case "badge":
        return <Typography>{cellData.driver?.badge}</Typography>;
      case "transporter_id":
        return <Typography>{cellData.driver?.transporter_id}</Typography>;
      case "company":
        return <Typography>{cellData.van?.van_group?.name || cellData.van?.van_group}</Typography>;
      case "da_status":
      case "second_da_status":
        return (
          <DaStatusField
            columnId={columnId}
            recordValue={recordValue}
            cellData={cellData}
            disabledCondition={disabledCondition}
            disabledByDate={disabledByDate}
            handleUpdateCell={handleUpdateCell}
          />
        );
      case "role":
        return (
          <Select
            fullWidth
            className={classes.selectFieldBig}
            variant="outlined"
            disabled={disabledByDate ? disabledCondition : cellData.da_status === 2}
            value={recordValue}
            onChange={(event: ChangeEvent<{ value: unknown }>) => {
              const role = event.target.value as number;
              if (role !== cellData.role) handleUpdateCell(getRolePatch(role, cellData, shiftStation));
            }}
          >
            {roleDa.filter((value) => value.roleId !== 5).map((el) => (
              <MenuItem key={el.roleId} value={el.value as number}>
                {el.name}
              </MenuItem>
            ))}
          </Select>
        );
      case "role_hours":
        return (
          <CellInput
            columnId={columnId}
            recordValue={recordValue}
            disabled={disabledCondition || !EDITABLE_HOURS_ROLES.includes(cellData.role)}
            classes={classes}
            type="number"
            handleUpdateCell={handleUpdateCell}
          />
        );
      case "fleet_size":
        return (
          <FleetSizeField
            cellData={cellData}
            shiftDate={shiftDate}
            shiftStation={shiftStation}
            disabledCondition={disabledCondition}
            handleUpdateCell={handleUpdateCell}
          />
        );
      case "van": {
        const isManuallyAssigned = assignment?.is_manually_assigned;
        return (
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography>{recordValue ? recordValue.number : "---"}</Typography>
            <ModalWindow
              buttonType={"custom"}
              modal_name={`edit-van-modal-${cellData.id}`}
              modalTitle={"Assign Fleet"}
              customButtonElement={
                <CustomIconButton
                  icon={<Edit />}
                  iconColor={!isManuallyAssigned ? "darkGray" : "white"}
                  theme={!isManuallyAssigned ? "gray" : "blue"}
                  disabled={
                    disabledCondition ||
                    (!cellData.driver && !CANCELLED_ROLES.includes(cellData.role))
                  }
                />
              }
            >
              <EditShiftVanModalContent
                shiftData={cellData}
                shiftDate={shiftDate}
                shiftStation={shiftStation}
                disabledCondition={disabledCondition}
              />
            </ModalWindow>
          </Box>
        );
      }
      case "route":
      case "number_of_stops":
      case "number_of_packages":
      case "number_of_pickups":
      case "number_of_completed_pickups":
      case "uta":
      case "utl":
      case "nsl":
      case "bc":
      case "otp_failure":
      case "missing":
      case "damage":
      case "missort":
      case "fdd":
      case "oc":
      case "others":
        return (
          <CellInput
            columnId={columnId}
            recordValue={recordValue}
            disabled={disabledCondition}
            classes={classes}
            type={columnId === "route" ? "text" : "number"}
            handleUpdateCell={handleUpdateCell}
          />
        );
      case "wave_time":
        return (
          <Select
            className={classes.editField}
            disabled={disabledCondition}
            value={recordValue}
            variant="outlined"
            onChange={(e) => handleUpdateCell({ [columnId]: Number(e.target.value) })}
          >
            {waveDa.map((el) => (
              <MenuItem key={el.id} value={el.value}>
                {el.name}
              </MenuItem>
            ))}
          </Select>
        );
      case "actual_DA":
        return (
          <Box className={classes.checkboxField}>
            <ModalWindow
              buttonType={"custom"}
              modal_name={`${cellData.id}-shift-comment-modal`}
              modalTitle="Comment"
              customButtonElement={
                <CustomIconButton
                  icon={<CommentOutlinedIcon />}
                  iconColor={!recordValue ? "darkGray" : "white"}
                  theme={!recordValue ? "gray" : "blue"}
                  disabled={disabledCondition}
                />
              }
            >
              <EditShiftCommentModalContent
                onSubmit={handleUpdateCell}
                commentText={(recordValue as string) || ""}
              />
            </ModalWindow>
          </Box>
        );
      case "rabbit_number":
      case "dolly_number": {
        const isRabbit = columnId === "rabbit_number";
        return (
          <SerialNumberField
            rowId={cellData.id}
            shiftStation={shiftStation}
            serialNumberName={isRabbit ? "rabbit_number" : "dolly_number"}
            secondSerialNumberName={isRabbit ? "second_rabbit_number" : "second_dolly_number"}
            returnName={isRabbit ? "returned_rabbit" : "returned_dolly"}
            firstSerialNumber={recordValue}
            secondSerialNumber={
              isRabbit ? cellData.second_rabbit_number : cellData.second_dolly_number
            }
            returnedSerial={isRabbit ? cellData.returned_rabbit : cellData.returned_dolly}
            disabledCondition={disabledCondition}
            classes={classes}
            handleUpdateCell={handleUpdateCell}
          />
        );
      }
      case "actual_end_time":
        return (
          <TextField
            type="time"
            variant="outlined"
            defaultValue="00:00"
            value={recordValue}
            disabled={disabledCondition}
            onFocus={(e) => {
              if (e.target.value !== recordValue) {
                handleUpdateCell({
                  [columnId]: momentTz().tz("America/Toronto").format("hh:mm"),
                });
              }
            }}
            onChange={(e) => {
              if (e.target.value !== recordValue) {
                handleUpdateCell({ [columnId]: e.target.value });
              }
            }}
          />
        );
      case "rescue":
      case "ontime_attendance":
      case "launchpad_support":
      case "morning_checklist":
        return (
          <Box className={classes.checkboxField}>
            <Checkbox
              checked={!!recordValue}
              value={!!recordValue}
              onChange={() => handleUpdateCell({ [columnId]: !recordValue })}
              disabled={disabledCondition}
            />
          </Box>
        );
      case "delete":
        return (
          <CustomIconButton
            icon={<CloseIcon />}
            iconColor={"red"}
            theme={"blank"}
            action={() => dispatch(removeReportRecordAction(cellData.id))}
          />
        );
      default:
        return <Typography>---</Typography>;
    }
  },
  areCellPropsEqual,
);

const DaStatusField: FC<{
  columnId: string;
  recordValue: number;
  cellData: ReportRecord;
  disabledCondition: boolean;
  disabledByDate: boolean;
  handleUpdateCell: UpdateCell;
}> = ({ columnId, recordValue, cellData, disabledCondition, disabledByDate, handleUpdateCell }) => {
  const highlight = useDaStatusHighlight(recordValue);
  const isDisabled =
    (disabledCondition && disabledByDate) ||
    (columnId === "da_status" ? !cellData.driver?.id : !cellData.second_driver?.id);

  return (
    <Alert
      icon={false}
      severity={highlight.severity as Color}
      className={isDisabled ? highlight.disabledClass : highlight.alertClassName}
      style={{ padding: "0 5px", display: "flex", justifyContent: "center" }}
    >
      <FormControl style={{ display: "flex", alignContent: "center" }} fullWidth>
        <Select
          style={{ width: 144 }}
          name="status"
          value={recordValue}
          defaultValue={recordValue}
          placeholder="status"
          disabled={isDisabled}
          disableUnderline
          onChange={(e) => {
            const statusValue = Number(e.target.value);
            if (statusValue !== recordValue) {
              handleUpdateCell(getDaStatusPatch(columnId, statusValue, cellData));
            }
          }}
        >
          {daShiftStatusConstants
            .filter(({ value }) => (recordValue === 3 ? true : value < 3))
            .map(({ value, option }) => (
              <MenuItem key={`action${option}`} value={value}>
                {option}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Alert>
  );
};

const FleetSizeField: FC<{
  cellData: ReportRecord;
  shiftDate: string;
  shiftStation: number;
  disabledCondition: boolean;
  handleUpdateCell: UpdateCell;
}> = ({ cellData, shiftDate, shiftStation, disabledCondition, handleUpdateCell }) => {
  const dispatch = useDispatch();
  const assignment = cellData.assignments?.[0];
  const fleetSizeValue = cellData.van?.fleet_size?.id
    ? cellData.van.fleet_size.id
    : cellData.cancelled_fleet_size || NO_FLEET_SIZE;

  const handleChange = async (event: ChangeEvent<{ value: any }>) => {
    const size = fleetSizesConstantList.find((el) => el.id === +event.target.value);

    if (CANCELLED_ROLES.includes(cellData.role)) {
      handleUpdateCell({ cancelled_fleet_size: event.target.value });
    }
    if (!size || cellData.role === 7 || !assignment?.id) return;

    try {
      const res = await api.setSizeToAssignment(assignment.id, {
        date: shiftDate,
        station: shiftStation,
        fleet_size: size.id,
      });
      if (res?.data) {
        dispatch(
          updateReportRecordStateAction(cellData.id, {
            ...cellData,
            assignments: [{ ...assignment, reason: 0, is_manually_assigned: false }],
            van: { ...res.data, fleet_size: size },
          }),
        );
      }
    } catch {
      dispatch(openModal("no-vans-modal"));
      dispatch(updateReportRecordStateAction(cellData.id, { ...cellData, van: null }));
    }
  };

  return (
    <Select
      fullWidth
      style={{ width: 160 }}
      name="size"
      variant="outlined"
      disabled={disabledCondition || (!cellData.driver && !CANCELLED_ROLES.includes(cellData.role))}
      value={fleetSizeValue}
      onChange={handleChange}
    >
      {fleetSizeValue === NO_FLEET_SIZE && (
        <MenuItem key={0} value={NO_FLEET_SIZE} selected disabled>
          No selection
        </MenuItem>
      )}
      {fleetSizesConstantList
        .filter((item) => ![4, 5].includes(item.id))
        .map((el: FleetSizeType) => (
          <MenuItem key={el.id} value={el.id}>
            {el.title}
          </MenuItem>
        ))}
    </Select>
  );
};

const CellInput: FC<{
  columnId: string;
  recordValue: any;
  disabled: boolean;
  classes: any;
  type: "number" | "text";
  handleUpdateCell: UpdateCell;
}> = ({ columnId, recordValue, disabled, classes, type, handleUpdateCell }) => {
  const [value, setValue] = useState(recordValue);

  useEffect(() => {
    setValue(recordValue);
  }, [recordValue]);

  return (
    <OutlinedInput
      className={classes.editField}
      value={![false, null, undefined].includes(value) ? value : type === "number" ? 0 : ""}
      onChange={(e) => setValue(type === "number" ? Number(e.target.value) : e.target.value)}
      onBlur={() => handleUpdateCell({ [columnId]: value })}
      type={type}
      disabled={disabled}
    />
  );
};

const DriverField: FC<{
  recordId: number;
  currentReportDate: string;
  currentReportStationId: number;
  driverValue: DriverData;
  assignmentId: number;
  daStatus: number;
  handleUpdateCell: UpdateCell;
  disabledCondition: boolean;
  isSecondDriver?: boolean;
}> = ({
  recordId,
  currentReportDate,
  currentReportStationId,
  driverValue,
  assignmentId,
  daStatus,
  handleUpdateCell,
  disabledCondition,
  isSecondDriver,
}) => {
  const [drivers, setDrivers] = useState<DriverData[]>([]);
  const [driverVanAssignmentId, setDriverVanAssignmentId] = useState(assignmentId);
  const [loading, setLoading] = useState(false);

  const clearDrivers = () => {
    setDrivers([]);
    setLoading(false);
  };

  const handleGetDrivers = () => {
    setLoading(true);
    api
      .getAvailableDrivers({
        date: currentReportDate,
        station: currentReportStationId,
        available: true,
      })
      .then((res) => {
        if (res?.data?.results) {
          setDrivers(res.data.results);
          setLoading(false);
        }
      })
      .catch(clearDrivers);
  };

  const handleChangeDriver = async (event: ChangeEvent<{ value: any }>) => {
    const driverId = event.target.value;

    if (isSecondDriver) {
      if (driverId !== driverValue?.id) {
        const currentDriver = drivers.find((el) => el.id === +driverId);
        handleUpdateCell({
          second_driver_id: currentDriver?.id || null,
          second_da_status: 0,
        });
      }
      return;
    }

    if (!recordId) return;
    const statusReset = { da_status: 0, is_status_published: false };

    if (driverId) {
      clearDrivers();
      const res = await api.setDriverToAssignment({
        record: recordId,
        driver: driverId,
        van: null,
      });
      if (res.data?.id) {
        handleUpdateCell(statusReset);
        setDriverVanAssignmentId(res.data.id);
      }
      return;
    }

    if (driverId === 0 && driverVanAssignmentId) {
      await api.deleteAssignment(driverVanAssignmentId);
    }
    handleUpdateCell(statusReset);
    setDriverVanAssignmentId(0);
  };

  return (
    <Select
      fullWidth
      variant="outlined"
      disabled={disabledCondition || daStatus === 3}
      value={driverValue?.id || 0}
      onOpen={handleGetDrivers}
      onChange={handleChangeDriver}
      onClose={clearDrivers}
      MenuProps={MENU_PROPS}
    >
      <MenuItem key={0} value={0}>
        Unassigned driver
      </MenuItem>
      {driverLabel(driverValue)}
      {loading ? (
        <MenuItem key="loading" disabled>
          <CircularProgress />
        </MenuItem>
      ) : (
        drivers.map((el) => (
          <MenuItem key={el.id} value={el.id}>
            {el.first_name} {el.last_name}
          </MenuItem>
        ))
      )}
    </Select>
  );
};

const SerialNumberField: FC<{
  rowId: number;
  shiftStation: number;
  serialNumberName: "dolly_number" | "rabbit_number";
  secondSerialNumberName: "second_dolly_number" | "second_rabbit_number";
  returnName: "returned_rabbit" | "returned_dolly";
  firstSerialNumber: any;
  secondSerialNumber: any;
  returnedSerial: boolean;
  disabledCondition: boolean;
  classes: any;
  handleUpdateCell: UpdateCell;
}> = memo(
  ({
    rowId,
    shiftStation,
    serialNumberName,
    secondSerialNumberName,
    returnName,
    firstSerialNumber,
    secondSerialNumber,
    returnedSerial,
    disabledCondition,
    classes,
    handleUpdateCell,
  }) => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const [loading, setLoading] = useState(false);
    const [serialNumberList, setSerialNumberList] = useState<DollyNumber[] | RabbitNumber[]>([]);
    const [isSecondActive, setIsSecondActive] = useState(false);

    const isSerialAvailable = (
      numId: number | null,
      value: RabbitNumber | DollyNumber,
      firstKey: keyof ReportRecord,
      secondKey: keyof ReportRecord,
    ) => {
      const currentReport = store.getState().dasBoard.currentReport;
      return (
        !currentReport.some(
          (record: any) => numId === record[firstKey]?.id || numId === record[secondKey]?.id,
        ) || numId === value?.id
      );
    };

    const handleGetSerial = () => {
      if (loading) return;
      setLoading(true);
      const fetchList =
        serialNumberName === "rabbit_number" ? api.getRabbitNumbers : api.getDollyNumbers;
      fetchList({ page_size: 1000, station: shiftStation, status: 1 })
        .then((res: any) => {
          if (res?.data?.results) setSerialNumberList(res.data.results);
        })
        .catch(() => {
          setSerialNumberList([]);
        })
        .finally(() => setLoading(false));
    };

    const updateSerial = (
      fieldName: "dolly_number" | "rabbit_number" | "second_dolly_number" | "second_rabbit_number",
      serialId: number,
    ) => {
      dispatch(
        updateSerialNumberAction(fieldName, rowId, { [fieldName]: serialId }, serialNumberList),
      );
    };

    const optionsFor = (current: any, otherId?: number) =>
      serialNumberList
        .filter((el) => isSerialAvailable(el.id, current, serialNumberName, secondSerialNumberName))
        .filter((el) => el.id !== otherId)
        .sort((a, b) => Number(a.serial_number) - Number(b.serial_number));

    const renderSelect = (
      value: any,
      fieldName: typeof serialNumberName | typeof secondSerialNumberName,
      otherId?: number,
    ) => (
      <Select
        className={classes.serialNumberSelect}
        variant="outlined"
        onOpen={handleGetSerial}
        disabled={disabledCondition}
        value={value?.id || 0}
        onChange={(e) => updateSerial(fieldName, Number(e.target.value))}
      >
        <MenuItem value={0}>---</MenuItem>
        {!serialNumberList.length ? serialLabel(value) : null}
        {loading ? (
          <MenuItem key="loading" disabled>
            <CircularProgress />
          </MenuItem>
        ) : (
          optionsFor(value, otherId).map((el) => (
            <MenuItem key={el.id} value={el.id}>
              {el.serial_number}
            </MenuItem>
          ))
        )}
      </Select>
    );

    return (
      <Box className={classes.serialNumberFields}>
        <Box style={{ display: "flex", alignItems: "center", gap: 24 }}>
          {renderSelect(firstSerialNumber, serialNumberName, secondSerialNumber?.id)}
          {!isSecondActive && !secondSerialNumber ? (
            <IconButton
              color="primary"
              onClick={() => setIsSecondActive(true)}
              disabled={disabledCondition}
            >
              <Add />
            </IconButton>
          ) : (
            renderSelect(secondSerialNumber, secondSerialNumberName, firstSerialNumber?.id)
          )}
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={returnedSerial}
              onChange={() => handleUpdateCell({ [returnName]: !returnedSerial })}
              disabled={disabledCondition}
            />
          }
          labelPlacement={"end"}
          label="Returned?"
        />
      </Box>
    );
  },
);

const driverLabel = (driver: DriverData | undefined) =>
  driver?.id ? (
    <MenuItem key={driver.id} value={driver.id}>
      {driver.first_name} {driver.last_name}
    </MenuItem>
  ) : (
    <MenuItem key={0} value={0}>
      Unassigned driver
    </MenuItem>
  );

const serialLabel = (serialNumber: DollyNumber | RabbitNumber | null | undefined) =>
  serialNumber?.id ? (
    <MenuItem key={serialNumber.id} value={serialNumber.id}>
      {serialNumber.serial_number}
    </MenuItem>
  ) : (
    <MenuItem key={0} value={0}>
      ---
    </MenuItem>
  );
